import { useCallback, useEffect, useRef, useState } from "react";
import type { Member, MemberAccountTransaction, PaymentMode } from "@/services/types";
import { memberService } from "@/services";
import { Modal } from "@/components/base/Modal";
import { Button } from "@/components/base/Button";
import { TextField } from "@/components/base/TextField";
import { Select } from "@/components/base/Select";
import { DatePicker } from "@/components/base/DatePicker";
import { Textarea } from "@/components/base/Textarea";
import { CurrencyInput } from "@/components/currency/CurrencyInput";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { useToast } from "@/context/ToastContext";

interface CollectionDepositModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (transaction: MemberAccountTransaction, member: Member) => void;
  initialMember?: Member | null;
}

const DEPOSIT_MODES: { value: PaymentMode; label: string }[] = [
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "cheque", label: "Cheque" },
];

function todayDateOnly(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function CollectionDepositModal({
  open,
  onClose,
  onSuccess,
  initialMember,
}: CollectionDepositModalProps) {
  const toast = useToast();
  const searchRef = useRef<HTMLInputElement>(null);
  const submittingRef = useRef(false);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Member[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(initialMember ?? null);
  const [loadingMember, setLoadingMember] = useState(false);

  // Form states
  const [amount, setAmount] = useState<number | null>(null);
  const [mode, setMode] = useState<PaymentMode>("cash");
  const [referenceNo, setReferenceNo] = useState("");
  const [receivedOn, setReceivedOn] = useState<string>(todayDateOnly());
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const referenceNeeded = mode !== "cash";

  // Reset or initialize on open
  useEffect(() => {
    if (open) {
      if (initialMember) {
        setSelectedMember(initialMember);
      } else {
        setSelectedMember(null);
      }
      setQuery("");
      setResults([]);
      setAmount(null);
      setMode("cash");
      setReferenceNo("");
      setReceivedOn(todayDateOnly());
      setRemarks("");
      setError(null);
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [open, initialMember]);

  // Live search debounced
  const runSearch = useCallback(async (q: string) => {
    if (!q.trim()) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    try {
      const data = await memberService.searchMembers(q);
      setResults(data);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => runSearch(query), 200);
    return () => window.clearTimeout(t);
  }, [query, runSearch]);

  const handleSelectMember = async (m: Member) => {
    setLoadingMember(true);
    setError(null);
    try {
      // Fetch fresh member record to ensure accurate account balance & enrollments
      const fullMember = await memberService.getMember(m.id);
      setSelectedMember(fullMember);
      setSearchOpen(false);
      setQuery("");
      setResults([]);
    } catch {
      setSelectedMember(m);
    } finally {
      setLoadingMember(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;

    if (!selectedMember) {
      setError("Please search and select a member first.");
      return;
    }

    if (!amount || amount <= 0) {
      setError("Please enter a valid deposit amount greater than zero.");
      return;
    }

    if (referenceNeeded && !referenceNo.trim()) {
      setError("Reference number is required for non-cash deposits.");
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    setError(null);

    try {
      const res = await memberService.depositAdvance(selectedMember.id, {
        amount,
        mode,
        referenceNo: referenceNo.trim() || undefined,
        receivedOn,
        remarks: remarks.trim() || undefined,
      });

      toast.success(
        "Deposit successful",
        `₹${amount.toLocaleString("en-IN")} deposited for ${selectedMember.fullName}.`
      );
      onSuccess(res.transaction, res.member);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not process deposit.");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Deposit Advance Money"
      description="Collect advance payment and add funds into member's advance account balance."
      size="md"
      footer={
        <div className="flex w-full items-center justify-between">
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={<i className="ri-check-line" />}
            loading={submitting}
            disabled={!selectedMember || !amount || amount <= 0 || (referenceNeeded && !referenceNo.trim())}
            onClick={handleSubmit}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            Confirm Deposit
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error ? (
          <div className="rounded-md border border-danger-200 bg-danger-50 p-2.5 text-xs text-danger-700">
            {error}
          </div>
        ) : null}

        {/* Member Selector / Search */}
        {!selectedMember ? (
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-foreground-700">
              Find Member <span className="text-danger-500">*</span>
            </label>
            <div className="relative">
              <TextField
                ref={searchRef}
                placeholder="Search member name, phone or code…"
                leftIcon="ri-search-line"
                value={query}
                autoFocus
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSearchOpen(true);
                }}
                onFocus={() => setSearchOpen(true)}
              />
              {searchOpen && results.length > 0 ? (
                <div className="absolute z-30 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-background-200 bg-white shadow-lg">
                  {results.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => handleSelectMember(m)}
                      className="flex w-full cursor-pointer items-center justify-between px-3 py-2 text-left hover:bg-background-50"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-foreground-900">{m.fullName}</p>
                        <p className="text-xs text-foreground-500 font-mono">{m.memberCode} · {m.city}</p>
                      </div>
                      <span className="tnum flex-none text-xs text-foreground-500 font-mono">{m.primaryPhone}</span>
                    </button>
                  ))}
                </div>
              ) : searchOpen && query.trim() && !searching ? (
                <div className="absolute z-30 mt-1 w-full rounded-md border border-background-200 bg-white p-3 text-center text-xs text-foreground-500 shadow-md">
                  No matching members found
                </div>
              ) : null}
            </div>
            <p className="text-[11px] text-foreground-400">
              Type member&apos;s name, mobile number, or ID to credit their account.
            </p>
          </div>
        ) : (
          /* Selected Member Banner */
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-800">
                  Member Selected
                </span>
                <p className="truncate text-sm font-bold text-foreground-950">{selectedMember.fullName}</p>
                <p className="text-xs text-foreground-600 font-mono">
                  {selectedMember.memberCode} · {selectedMember.primaryPhone}
                </p>
              </div>
              <div className="text-right">
                <span className="text-[10px] font-medium text-foreground-500 uppercase tracking-wider block">
                  {(selectedMember.accountBalance ?? 0) < 0 ? "Wallet Deficit" : "Current Advance"}
                </span>
                <span className={`text-sm font-bold ${(selectedMember.accountBalance ?? 0) < 0 ? "text-danger-600 font-bold" : "text-emerald-800"}`}>
                  <CurrencyDisplay amount={selectedMember.accountBalance ?? 0} />
                </span>
                <div>
                  <button
                    type="button"
                    onClick={() => setSelectedMember(null)}
                    className="mt-1 cursor-pointer text-[11px] text-primary-600 underline hover:text-primary-700"
                  >
                    Change member
                  </button>
                </div>
              </div>
            </div>
            {(selectedMember.accountBalance ?? 0) < 0 && (amount ?? 0) > 0 ? (
              <div className="mt-2 pt-2 border-t border-emerald-200/60 flex items-center justify-between text-xs">
                <span className="text-foreground-600">Balance after deposit:</span>
                <span className={`font-bold ${(selectedMember.accountBalance ?? 0) + (amount ?? 0) < 0 ? "text-danger-600" : (selectedMember.accountBalance ?? 0) + (amount ?? 0) === 0 ? "text-foreground-900" : "text-success-700"}`}>
                  <CurrencyDisplay amount={(selectedMember.accountBalance ?? 0) + (amount ?? 0)} />
                  {(selectedMember.accountBalance ?? 0) + (amount ?? 0) >= 0 ? " (Deficit Cleared)" : " (Remaining Deficit)"}
                </span>
              </div>
            ) : null}
          </div>
        )}

        {/* Deposit Inputs */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <CurrencyInput
              label="Deposit Amount"
              value={amount}
              onChange={setAmount}
              placeholder="0.00"
              required
              disabled={!selectedMember || submitting}
              autoFocus={!!selectedMember}
            />
          </div>

          <div>
            <Select
              label="Payment Mode"
              value={mode}
              onChange={(v) => setMode(v as PaymentMode)}
              options={DEPOSIT_MODES}
              disabled={!selectedMember || submitting}
            />
          </div>

          <div>
            <DatePicker
              label="Deposit Date"
              value={receivedOn}
              onChange={setReceivedOn}
              disabled={!selectedMember || submitting}
            />
          </div>

          {referenceNeeded ? (
            <div className="sm:col-span-2">
              <TextField
                label="Reference Number / UTR"
                placeholder="e.g. UPI Ref / Cheque No / Bank Txn ID"
                value={referenceNo}
                onChange={(e) => setReferenceNo(e.target.value)}
                required
                disabled={!selectedMember || submitting}
              />
            </div>
          ) : null}

          <div className="sm:col-span-2">
            <Textarea
              label="Remarks / Note (Optional)"
              placeholder="e.g. Advance paid for upcoming cycles"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={2}
              disabled={!selectedMember || submitting}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
}
